import { computed, ref } from 'vue'
import { useMutation } from '@vue/apollo-composable'

import { graphql } from '@/shared/graphql/router'
import { formatPrice } from '@/shared/utils/money'
import { getStripe } from '@/shared/services/stripe'
import { useBillingStore } from '@/features/billing/store'
import { useUserStore } from '@/shared/stores/user'
import { useWebviewStore } from '@/shared/stores/webview'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import {
  openDialog,
  closeDialog,
  closeAllDialogs,
  Dialog,
} from '@/shared/dialogs/dialog'
import { openModal, closeAllModals, Modals } from '@/shared/dialogs/modals'

const PURCHASE_REPORT_CREDITS_MUTATION = graphql(`
  mutation PurchaseReportCredits($input: PurchaseReportCreditsInput!) {
    purchaseReportCredits(input: $input) {
      requiresAction {
        clientSecret
      }
      error {
        code
        message
      }
    }
  }
`)

export interface PayReportProduct {
  id: string
  credits: number
  price: { amount: number; currencyCode: string }
}

export const usePayReportCredits = (
  product: PayReportProduct,
  afterPurchase?: () => void
) => {
  const billingStore = useBillingStore()
  const userStore = useUserStore()
  const webviewStore = useWebviewStore()
  const toastStore = useToastStore()

  const loading = ref(false)
  const error = ref('')

  const paymentMethod = computed(() => billingStore.billing?.paymentMethod)

  const total = computed(() =>
    formatPrice({
      amount: product.price.amount / 100,
      currency: product.price.currencyCode,
    })
  )

  const { mutate: purchaseReportCredits } = useMutation(
    PURCHASE_REPORT_CREDITS_MUTATION,
    { clientId: 'router' }
  )

  const handleChangePayment = () => {
    error.value = ''
    openDialog(Dialog.AddPaymentMethod, { stack: true })
  }

  const confirmAction = async (clientSecret: string) => {
    if (webviewStore.isActive) return true

    const stripe = await getStripe()
    if (!stripe) return false

    const { error: stripeError } = await stripe.confirmCardPayment(clientSecret)
    if (stripeError) {
      toastStore.showToast({
        type: ToastType.Error,
        content: 'Payment authentication failed',
        error: new Error(stripeError.message),
      })
      return false
    }
    return true
  }

  const handleContinue = () => {
    closeAllModals()
    if (afterPurchase) {
      afterPurchase()
    } else {
      openDialog(Dialog.CreateReport)
    }
  }

  const handlePay = async () => {
    try {
      loading.value = true
      error.value = ''

      const response = await purchaseReportCredits({
        input: { productId: product.id },
      })
      const result = response?.data?.purchaseReportCredits

      if (result?.error) {
        error.value = result.error.message || 'Payment failed'
        loading.value = false
        return
      }

      if (result?.requiresAction) {
        const success = await confirmAction(result.requiresAction.clientSecret)
        if (!success) {
          loading.value = false
          return
        }
      }

      userStore.updateUser({
        reportCredits: (userStore.user?.reportCredits || 0) + product.credits,
      })

      closeAllDialogs()
      openModal(Modals.PurchaseCreditsSuccess, {
        events: { success: handleContinue },
      })
    } catch (err) {
      loading.value = false
      error.value = (err as Error).message
    }
  }

  const handleChange = () => closeDialog()
  const handleClose = () => closeAllDialogs()

  return {
    loading,
    error,
    paymentMethod,
    total,
    handleChangePayment,
    handlePay,
    handleChange,
    handleClose,
  }
}
